// Error Logging Service
// This service centralizes error logging and can integrate with services like Sentry

class ErrorLoggingService {
  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production';
    this.userId = null;
    this.userEmail = null;
    
    // Initialize error logging service (e.g., Sentry)
    if (this.isProduction) {
      // Example: Sentry.init({ dsn: 'your-sentry-dsn' });
    }
  }

  // Set user context for error logs
  setUserContext(user) {
    this.userId = user?.id;
    this.userEmail = user?.email;
    
    if (this.isProduction) {
      // Example: Sentry.setUser({ id: user.id, email: user.email });
    }
  }

  // Log JavaScript errors
  logError(error, context = {}) {
    const errorData = {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      userId: this.userId,
      userEmail: this.userEmail,
      url: window.location.href,
      userAgent: navigator.userAgent,
      context
    };

    // Always log to console in development
    if (!this.isProduction) {
      console.error('Error logged:', errorData);
    }

    // Send to external service in production
    if (this.isProduction) {
      // Example: Sentry.captureException(error, { extra: context });
      this._sendToLoggingService(errorData);
    }
  }

  // Log API errors specifically
  logApiError(error, endpoint, requestData = {}) {
    const apiErrorData = {
      type: 'API_ERROR',
      endpoint,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      requestData,
      timestamp: new Date().toISOString()
    };

    this.logError(error, apiErrorData);
  }

  // Log authentication errors
  logAuthError(error, action) {
    const authErrorData = {
      type: 'AUTH_ERROR',
      action, // 'login', 'register', 'token_refresh', etc.
      timestamp: new Date().toISOString()
    };

    this.logError(error, authErrorData);
  }

  // Log performance issues
  logPerformanceIssue(metric, value, threshold) {
    const performanceData = {
      type: 'PERFORMANCE_ISSUE',
      metric,
      value,
      threshold,
      timestamp: new Date().toISOString()
    };

    if (this.isProduction) {
      // Send performance data to monitoring service
      this._sendToLoggingService(performanceData);
    }
  }

  // Private method to send data to external logging service
  _sendToLoggingService(data) {
    // This would integrate with your chosen logging service
    // Example implementations:
    
    // For Sentry:
    // Sentry.addBreadcrumb({ message: 'Custom error', data });
    
    // For custom API:
    // fetch('/api/logs', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(data)
    // }).catch(err => console.error('Failed to send log:', err));
    
    console.log('Would send to logging service:', data);
  }

  // Clear user context on logout
  clearUserContext() {
    this.userId = null;
    this.userEmail = null;
    
    if (this.isProduction) {
      // Example: Sentry.configureScope(scope => scope.clear());
    }
  }
}

// Create singleton instance
const errorLoggingService = new ErrorLoggingService();

export default errorLoggingService;
